import React, { useMemo, useState } from 'react';
import { ArrowRight, CircleCheck, Download, FileText, ShieldCheck, TriangleAlert } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { HEADER_SPELLINGS, LeadsTile, QualityBar, SheetGlyph, SheetTile, StatTile } from './SheetImportDetailsModal';

const SHELL: React.CSSProperties = {
  background: G.shellBg,
  border: `1px solid ${G.shellBorder}`,
  borderRadius: '13px',
  padding: '4.6px',
};
const INNER: React.CSSProperties = {
  background: C.white,
  border: `1px solid ${G.line}`,
  borderRadius: '9.2px',
};

const HOW_STEPS = [
  { t: 'Share the sheet', d: 'Set access to “Anyone with the link → Viewer”.' },
  { t: 'Paste the link', d: 'Drop the sheet URL above and hit Sync.' },
  { t: 'Auto-map & validate', d: 'Headers match lead fields; every row is checked.' },
  { t: 'Review & push', d: 'Approve the ready rows and they land in Leads.' },
];

const MAPPING_EXAMPLES: [string, string][] = [
  ['Full Name', 'name'],
  ['Mobile No', 'phone'],
  ['Email ID', 'email'],
  ['Location', 'city'],
  ['Lead Source', 'source'],
];

// ── Dummy data (same file the user can download) ─────────────────────────────
const CSV_FILE_NAME = 'sample-leads.csv';
const HEADERS = ['name', 'phone', 'email', 'source', 'status', 'notes', 'city', 'batch', 'priority'];
const ROWS: string[][] = [
  ['Rahul Sharma', '9876543210', 'rahul.sharma@gmail.com', 'Website', 'new', 'Interested in premium plan', 'Srinagar', 'Batch-A', 'High'],
  ['Priya Koul', '7006123456', 'priya.koul@yahoo.com', 'Referral', 'contacted', 'Follow up next week', 'Jammu', 'Batch-A', 'Medium'],
  ['Amit Verma', '9906789012', 'amit.verma@outlook.com', 'Instagram', 'new', '', 'Anantnag', 'Batch-B', 'Low'],
  ['', '8899776655', 'missing.name@gmail.com', 'Facebook', 'new', 'Name is missing in this row', 'Baramulla', 'Batch-B', 'Medium'],
  ['Vikram Singh', 'invalid_phone', 'vikram.singh@gmail.com', 'Website', 'new', 'This phone number is invalid', 'Pulwama', 'Batch-A', 'High'],
];

// What the demo Google Form collects (first sample row, pre-filled) → sheet column
const FORM_FIELDS: { q: string; value: string; col: string; required?: boolean; long?: boolean }[] = [
  { q: 'Full name', value: 'Rahul Sharma', col: 'name', required: true },
  { q: 'Phone number', value: '9876543210', col: 'phone', required: true },
  { q: 'Email address', value: 'rahul.sharma@gmail.com', col: 'email' },
  { q: 'City', value: 'Srinagar', col: 'city' },
  { q: 'How did you hear about us?', value: 'Website', col: 'source' },
  { q: 'Anything we should know?', value: 'Interested in premium plan', col: 'notes', long: true },
];

const esc = (v: string) => (/[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v);
const CSV_TEXT = [HEADERS, ...ROWS].map((r) => r.map(esc).join(',')).join('\n') + '\n';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const PHONE_RE = /^\d{10}$/;
const checkRow = (r: string[]): string | null => {
  if (!r[0].trim()) return 'Name is missing';
  if (!PHONE_RE.test(r[1].trim())) return 'Invalid phone number';
  if (r[2].trim() && !EMAIL_RE.test(r[2].trim())) return 'Invalid email format';
  return null;
};

const downloadCsv = () => {
  const blob = new Blob([CSV_TEXT], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = CSV_FILE_NAME;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

const sectionTitle = (text: string) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '7.4px' }}>
    <div
      style={{
        width: '3px',
        height: '14.8px',
        background: `linear-gradient(180deg,${C.lime},#8aaa00)`,
        borderRadius: '3px',
      }}
    />
    <span style={{ fontSize: '0.78rem', fontWeight: 700, color: C.teal }}>{text}</span>
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

const SheetImportsDocs: React.FC = () => {
  const [formSent, setFormSent] = useState(false);

  const result = useMemo(() => {
    const checked = ROWS.map((r, i) => ({ row: i + 2, name: r[0], reason: checkRow(r) }));
    const invalid = checked.filter((c) => c.reason);
    return { total: ROWS.length, invalid, valid: ROWS.length - invalid.length };
  }, []);

  const invalidRows = new Set(result.invalid.map((i) => i.row));

  const cell: React.CSSProperties = {
    border: '1px solid #e2e3e3',
    padding: '4px 8px',
    whiteSpace: 'nowrap',
    fontSize: '0.66rem',
    color: '#202124',
    background: '#fff',
    height: '22px',
  };
  const gutter: React.CSSProperties = {
    ...cell,
    background: '#f8f9fa',
    color: '#5f6368',
    textAlign: 'center',
    fontWeight: 500,
    minWidth: '28px',
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '11px' }}>
      <style>{`
        .shi-step-line { flex:1; height:2px; min-width:14px; background: linear-gradient(90deg, ${C.lime}, rgba(17,85,80,0.15)); border-radius:2px; margin-top:13px; }
        @media (max-width: 720px) { .shi-step-line { display:none; } }
        .shi-doc-btn:hover { transform: translateY(-1px); box-shadow: 0 4px 14px rgba(200,233,75,0.5) !important; }
      `}</style>

      {/* ── How it works + mapping ─────────────────────────────────── */}
      <div style={{ display: 'flex', gap: '11px', flexWrap: 'wrap' }}>
        <div style={{ ...SHELL, flex: '2 1 460px' }}>
          <div
            style={{
              ...INNER,
              padding: '14px 16.6px',
              height: '100%',
              boxSizing: 'border-box',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {sectionTitle('How the sync works')}
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', marginTop: '14px', flexWrap: 'wrap' }}>
              {HOW_STEPS.map((s, i) => (
                <React.Fragment key={s.t}>
                  <div style={{ flex: '1 1 110px', minWidth: '110px' }}>
                    <div
                      style={{
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        background: i === HOW_STEPS.length - 1 ? C.lime : 'rgba(17,85,80,0.08)',
                        border: `1px solid ${i === HOW_STEPS.length - 1 ? C.limeBorder : 'rgba(17,85,80,0.2)'}`,
                        color: C.teal,
                        fontSize: '0.72rem',
                        fontWeight: 800,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      {i + 1}
                    </div>
                    <div style={{ fontSize: '0.74rem', fontWeight: 700, color: C.teal, marginTop: '8px' }}>{s.t}</div>
                    <div style={{ fontSize: '0.66rem', color: C.sub, lineHeight: 1.5, marginTop: '2.8px' }}>{s.d}</div>
                  </div>
                  {i < HOW_STEPS.length - 1 && <div className="shi-step-line" />}
                </React.Fragment>
              ))}
            </div>
            <div
              style={{
                marginTop: 'auto',
                paddingTop: '14px',
                display: 'flex',
                alignItems: 'center',
                gap: '6.5px',
                flexWrap: 'wrap',
                fontSize: '0.66rem',
                color: C.sub,
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5.5px',
                  padding: '5.5px 10.1px',
                  borderRadius: '7.4px',
                  background: 'rgba(22,163,74,0.06)',
                  border: '1px solid rgba(22,163,74,0.2)',
                  color: '#15803d',
                  fontWeight: 600,
                }}
              >
                <ShieldCheck size={13} /> Read-only access — your sheet is never changed
              </span>
              <span>Re-sync anytime; every sync is kept in Sync History.</span>
            </div>
          </div>
        </div>

        <div style={{ ...SHELL, flex: '1 1 260px' }}>
          <div style={{ ...INNER, padding: '14px 16.6px', height: '100%', boxSizing: 'border-box' }}>
            {sectionTitle('Smart column mapping')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '5.5px', marginTop: '12px' }}>
              {MAPPING_EXAMPLES.map(([from, to]) => (
                <div key={from} style={{ display: 'flex', alignItems: 'center', gap: '7.4px', fontSize: '0.69rem' }}>
                  <span
                    style={{
                      flex: 1,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '5.5px',
                      padding: '4.6px 8.3px',
                      borderRadius: '6.5px',
                      background: G.shellBg,
                      border: `1px solid ${G.line}`,
                      color: '#374151',
                      fontWeight: 600,
                    }}
                  >
                    <SheetGlyph size={11} color="#16a34a" /> {from}
                  </span>
                  <ArrowRight size={11} color={C.muted} />
                  <code
                    style={{
                      flex: '0 0 62px',
                      textAlign: 'center',
                      padding: '4.6px 0',
                      borderRadius: '6.5px',
                      background: C.limeDim,
                      border: `1px solid ${C.limeBorder}`,
                      color: '#4a5e00',
                      fontWeight: 700,
                    }}
                  >
                    {to}
                  </code>
                </div>
              ))}
            </div>
            <div style={{ fontSize: '0.64rem', color: C.muted, marginTop: '9.2px', lineHeight: 1.5 }}>
              {HEADER_SPELLINGS} header spellings recognised. <strong style={{ color: C.sub }}>name</strong> and{' '}
              <strong style={{ color: C.sub }}>phone</strong> are required.
            </div>
          </div>
        </div>
      </div>

      {/* ── Data flow demo ─────────────────────────────────────────── */}
      <div style={SHELL}>
        <div style={{ ...INNER, padding: '14px 16.6px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9.2px', flexWrap: 'wrap' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '3px' }}>
              {sectionTitle('Try it with sample data')}
              <span style={{ fontSize: '0.66rem', color: C.sub, marginLeft: '10.4px' }}>
                A Google Form collects leads, its responses fill a Google Sheet, and the sync reads that sheet. Download the 5-row demo
                CSV, import it into a new Google Sheet, share it as “Anyone with the link → Viewer”, then paste the link above.
              </span>
            </div>
          </div>

          {/* Flow strip: Form → Sheet → Validate → Leads */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '12px',
              flexWrap: 'wrap',
              padding: '12px',
              borderRadius: '10px',
              background: G.shellBg,
              border: `1px solid ${G.line}`,
            }}
          >
            {[
              {
                icon: (
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: '#673ab7', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <FileText size={17} color="#fff" />
                  </div>
                ),
                t: '1 · Google Form',
                d: 'People submit their details',
              },
              { icon: <SheetTile size={34} />, t: '2 · Google Sheet', d: `${ROWS.length} rows · ${HEADERS.length} columns` },
              {
                icon: (
                  <div style={{ width: 34, height: 34, borderRadius: 9, background: 'rgba(17,85,80,0.08)', border: '1px solid rgba(17,85,80,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <ShieldCheck size={17} color={C.teal} />
                  </div>
                ),
                t: '3 · Map & validate',
                d: `${result.valid} ready · ${result.invalid.length} invalid`,
              },
              { icon: <LeadsTile size={34} />, t: '4 · Leads', d: 'After you review & push' },
            ].map((st, i, arr) => (
              <React.Fragment key={st.t}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {st.icon}
                  <div>
                    <div style={{ fontSize: '0.7rem', fontWeight: 700, color: C.teal }}>{st.t}</div>
                    <div style={{ fontSize: '0.62rem', color: C.sub }}>{st.d}</div>
                  </div>
                </div>
                {i < arr.length - 1 && <ArrowRight size={16} color={C.muted} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Google Form mock */}
          {sectionTitle('1 · Collect leads with a Google Form')}
          <div style={{ display: 'flex', gap: '14px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
            <div
              style={{
                flex: '1 1 340px',
                maxWidth: '520px',
                padding: '14px',
                borderRadius: '10px',
                background: '#f0ebf8',
                border: '1px solid #e0d7f0',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px',
              }}
            >
              <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #dadce0', borderTop: '9px solid #673ab7', padding: '14px 16px' }}>
                <div style={{ fontSize: '1.05rem', fontWeight: 500, color: '#202124' }}>Lead Enquiry Form</div>
                <div style={{ fontSize: '0.66rem', color: '#5f6368', marginTop: '4px' }}>
                  Fill in your details and our team will get back to you.
                </div>
                <div style={{ fontSize: '0.6rem', color: '#d93025', marginTop: '8px' }}>* Indicates required question</div>
              </div>

              {formSent ? (
                <div style={{ background: '#fff', borderRadius: '8px', border: '1px solid #dadce0', padding: '16px' }}>
                  <div style={{ fontSize: '0.8rem', color: '#202124' }}>Your response has been recorded.</div>
                  <div style={{ fontSize: '0.66rem', color: '#137333', marginTop: '6px' }}>
                    ✓ Added to the linked Google Sheet as row 2. The sync will pick it up next time you run it.
                  </div>
                  <button
                    onClick={() => setFormSent(false)}
                    style={{ marginTop: '10px', background: 'none', border: 'none', padding: 0, color: '#673ab7', fontSize: '0.68rem', cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Submit another response
                  </button>
                </div>
              ) : (
                <>
                  {FORM_FIELDS.map((f) => (
                    <div key={f.col} style={{ background: '#fff', borderRadius: '8px', border: '1px solid #dadce0', padding: '12px 16px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                        <span style={{ fontSize: '0.74rem', color: '#202124' }}>
                          {f.q} {f.required && <span style={{ color: '#d93025' }}>*</span>}
                        </span>
                        <code
                          style={{
                            fontSize: '0.58rem',
                            padding: '2px 7px',
                            borderRadius: '10px',
                            background: C.limeDim,
                            border: `1px solid ${C.limeBorder}`,
                            color: '#4a5e00',
                            fontWeight: 700,
                          }}
                        >
                          → column: {f.col}
                        </code>
                      </div>
                      <div
                        style={{
                          marginTop: '8px',
                          paddingBottom: '4px',
                          borderBottom: '1px solid #dadce0',
                          fontSize: '0.72rem',
                          color: '#3c4043',
                          minHeight: f.long ? '30px' : undefined,
                        }}
                      >
                        {f.value}
                      </div>
                    </div>
                  ))}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap', marginTop: '2px' }}>
                    <button
                      onClick={() => setFormSent(true)}
                      style={{
                        padding: '7px 22px',
                        borderRadius: '4px',
                        border: 'none',
                        background: '#673ab7',
                        color: '#fff',
                        fontSize: '0.72rem',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Submit
                    </button>
                    <span style={{ fontSize: '0.58rem', color: '#5f6368' }}>Demo only — nothing is actually sent</span>
                  </div>
                </>
              )}
            </div>

            <div style={{ flex: '1 1 220px', fontSize: '0.68rem', color: C.sub, lineHeight: 1.6, display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <div style={{ fontWeight: 700, color: C.teal, fontSize: '0.74rem' }}>How the form connects</div>
              <div>1. Create the form and add one question per lead field.</div>
              <div>2. In the form’s <strong>Responses</strong> tab choose <strong>Link to Sheets</strong>.</div>
              <div>3. Each submission becomes a new row; question titles become the sheet headers.</div>
              <div>4. Share that sheet (Anyone with the link → Viewer) and paste its link in Sync.</div>
              <div style={{ color: C.muted }}>
                Columns like <code>status</code>, <code>batch</code> and <code>priority</code> aren’t on the form — your team adds them in the sheet.
              </div>
            </div>
          </div>

          {/* Step 2: sheet / csv */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '9.2px', flexWrap: 'wrap' }}>
            {sectionTitle('2 · Responses land in a Google Sheet')}
            <button
              onClick={downloadCsv}
              className="shi-doc-btn"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '8px 16px',
                background: C.lime,
                border: 'none',
                borderRadius: '8px',
                color: C.teal,
                fontSize: '0.7rem',
                fontWeight: 800,
                letterSpacing: '0.3px',
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(200,233,75,0.35)',
                transition: 'all 0.2s',
                whiteSpace: 'nowrap',
              }}
            >
              <Download size={13} /> DOWNLOAD CSV
            </button>
          </div>

          {(
            <div style={{ border: '1px solid #dadce0', borderRadius: '10px', overflow: 'hidden', background: '#fff' }}>
              {/* Window chrome */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '8px 12px', background: '#f1f3f4', borderBottom: '1px solid #dadce0' }}>
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#ff5f57' }} />
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#febc2e' }} />
                <span style={{ width: 9, height: 9, borderRadius: '50%', background: '#28c840' }} />
                <span
                  style={{
                    marginLeft: '10px',
                    flex: 1,
                    maxWidth: '420px',
                    padding: '3px 10px',
                    borderRadius: '12px',
                    background: '#fff',
                    border: '1px solid #e0e0e0',
                    fontSize: '0.62rem',
                    color: '#5f6368',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  docs.google.com/spreadsheets/d/1AbC…demo/edit
                </span>
              </div>
              {/* Title + menu */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px 4px' }}>
                <SheetGlyph size={22} color="#16a34a" />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ fontSize: '0.8rem', fontWeight: 500, color: '#202124' }}>Lead Enquiry Form (Responses)</span>
                  <span style={{ fontSize: '0.62rem', color: '#5f6368', display: 'flex', gap: '10px', marginTop: '2px' }}>
                    <span>File</span>
                    <span>Edit</span>
                    <span>View</span>
                    <span>Insert</span>
                    <span>Format</span>
                    <span>Data</span>
                  </span>
                </div>
                <span
                  style={{
                    marginLeft: 'auto',
                    padding: '4px 12px',
                    borderRadius: '14px',
                    background: '#c2e7ff',
                    color: '#001d35',
                    fontSize: '0.62rem',
                    fontWeight: 600,
                  }}
                >
                  Anyone with the link · Viewer
                </span>
              </div>
              {/* Grid */}
              <div style={{ overflow: 'auto', maxHeight: '340px' }}>
                <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                  <thead>
                    <tr>
                      <th style={{ ...gutter, position: 'sticky', top: 0, zIndex: 2 }} />
                      {HEADERS.map((_, i) => (
                        <th key={i} style={{ ...gutter, position: 'sticky', top: 0, zIndex: 1 }}>
                          {String.fromCharCode(65 + i)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td style={gutter}>1</td>
                      {HEADERS.map((h) => (
                        <td key={h} style={{ ...cell, fontWeight: 700, background: '#f8f9fa' }}>
                          {h}
                        </td>
                      ))}
                    </tr>
                    {ROWS.map((r, ri) => (
                      <tr key={ri}>
                        <td style={gutter}>{ri + 2}</td>
                        {r.map((v, ci) => (
                          <td key={ci} style={cell}>
                            {v}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {/* Sheet tab bar */}
              <div style={{ display: 'flex', alignItems: 'center', padding: '5px 12px', background: '#f8f9fa', borderTop: '1px solid #dadce0' }}>
                <span
                  style={{
                    padding: '3px 14px',
                    borderRadius: '4px',
                    background: '#e6f4ea',
                    color: '#137333',
                    fontSize: '0.64rem',
                    fontWeight: 600,
                  }}
                >
                  Sheet1
                </span>
              </div>
            </div>
          )}

          {/* What the sync produces */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {sectionTitle('3 · What the sync produces from this file')}
            <div style={{ display: 'flex', gap: '6.5px', flexWrap: 'wrap' }}>
              <StatTile label="Rows Read" value={result.total} />
              <StatTile label="Ready" value={result.valid} color="#15803d" />
              <StatTile label="Invalid" value={result.invalid.length} color="#c62828" />
            </div>
            <QualityBar valid={result.valid} invalid={result.invalid.length} showLabels={false} />

            <div style={{ display: 'flex', flexDirection: 'column', gap: '5px', marginTop: '2px' }}>
              {result.invalid.map((i) => (
                <div
                  key={i.row}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '6px 10px',
                    borderRadius: '7px',
                    background: 'rgba(198,40,40,0.05)',
                    border: '1px solid rgba(198,40,40,0.18)',
                    fontSize: '0.68rem',
                    color: '#7f1d1d',
                  }}
                >
                  <TriangleAlert size={12} color="#c62828" />
                  <strong>Row {i.row}</strong>
                  <span style={{ color: C.sub }}>{i.name || '(no name)'}</span>
                  <ArrowRight size={10} color={C.muted} />
                  <span>{i.reason}</span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 10px',
                  borderRadius: '7px',
                  background: 'rgba(22,163,74,0.06)',
                  border: '1px solid rgba(22,163,74,0.2)',
                  fontSize: '0.68rem',
                  color: '#14532d',
                }}
              >
                <CircleCheck size={12} color="#16a34a" />
                {result.valid} rows are staged for review. Nothing reaches Leads until you push.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SheetImportsDocs;